/* /_emails — the message inventory, rendered for review (Step 7).
   System route, Orchestrator-owned (like /_systeme). Each template renders in
   an iframe exactly as it would land in an inbox (the brand shell, inline
   styles, 600 px). The delivery screens themselves (bibliothèque, studio,
   centre de notifications) are WF-11 — Builder A’s scope; this page is the
   review surface for the e-mails those screens will manage. */
import { useMemo, useState } from 'react'
import { Button, Note, Pill } from '@/ui'
import { TEMPLATES, giftEmail, DEMO_GIFT } from '@/emails/templates'
import { getRoues, drawFromRoue } from '@/lib/wheel'
import { renderEmail } from '@/emails/render'

const CLASS_ORDER = ['transactionnel', 'rappel', 'avis', 'marketing', 'compte', 'marchand'] as const

export default function Emails() {
  const [current, setCurrent] = useState(TEMPLATES[0].id)
  /* M1 · the gift e-mail reads the reward from a draw on the active roue — « Tirer un autre cadeau » proves it */
  const [drawn, setDrawn] = useState(DEMO_GIFT)
  const redraw = () => {
    const roue = getRoues().find(w => w.usedBy.some(u => u.startsWith('Rentrée 2026'))) ?? getRoues()[0]
    const x = roue ? drawFromRoue(roue) : null
    if (x) setDrawn({ ...DEMO_GIFT, prize: x.name.charAt(0).toUpperCase() + x.name.slice(1) })
  }
  const t = current === 'cadeau' ? giftEmail(drawn) : TEMPLATES.find(x => x.id === current)!
  const html = useMemo(() => renderEmail(t), [t])

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 1060 }}>
      <div>
        <h2 className="t-page" style={{ margin: 0 }}>Messages — inventaire des envois</h2>
        <p className="f-help" style={{ marginTop: 6 }}>
          Un seul gabarit (packages/brand/email/shell.html), deux en-têtes&nbsp;: Ink + spark pour le cadeau, Paper pour tout le reste.
          Quatre classes client aux règles séparées, appliquées à l’envoi — pas seulement à l’écran.
        </p>
        {current === 'cadeau' && <p className="f-help" style={{ margin: '6px 0 0' }}>Le nom du cadeau vient du tirage de la roue en service — <a href="#" onClick={e => { e.preventDefault(); redraw() }}>tirer un autre cadeau</a> · actuellement «&nbsp;{drawn.prize}&nbsp;»</p>}
      </div>

      {/* two columns on desktop; stacks whole when 2×300 no longer fits (phone) */}
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
          {CLASS_ORDER.map(c => {
            const group = TEMPLATES.filter(x => x.classe === c)
            if (group.length === 0) return null
            return (
              <div key={c}>
                <p className="t-eyebrow" style={{ margin: '0 0 6px' }}>{group[0].classeLabel}</p>
                <div style={{ display: 'grid', gap: 6 }}>
                  {group.map(x => (
                    <button key={x.id} onClick={() => setCurrent(x.id)} style={{
                      textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '10px 12px',
                      border: `1px solid ${x.id === current ? 'var(--bmr-action)' : 'var(--bmr-border)'}`,
                      background: x.id === current ? 'var(--bmr-selected)' : 'var(--bmr-surface)' }}>
                      <span style={{ font: '600 13px var(--bmr-font-ui)', color: 'var(--bmr-text)' }}>{x.name}</span>
                      <span className="f-help" style={{ display: 'block', marginTop: 2 }}>
                        {x.copyStatus === 'verbatim' ? '● copy verbatim (wireframe)' : '✎ brouillon — relecture FR Phase 3'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ font: '700 14px var(--bmr-font-ui)' }}>{t.subject}</span>
            <Pill tone={t.copyStatus === 'verbatim' ? 'ok' : 'warn'}>
              {t.copyStatus === 'verbatim' ? 'Verbatim' : 'Brouillon'}
            </Pill>
            <span className="f-help" style={{ marginLeft: 'auto' }}>{t.frameRef}</span>
          </div>
          <p className="f-help" style={{ margin: 0 }}>De&nbsp;: {t.from} · Pré-en-tête&nbsp;: «&nbsp;{t.preheader}&nbsp;»</p>
          <iframe title={t.name} srcDoc={html} style={{
            width: '100%', height: 640, border: '1px solid var(--bmr-border)',
            borderRadius: 14, background: '#EFEDE5' }} />
          <Note tone="candour"><b>Règle d’envoi&nbsp;:</b> {t.sendRule}</Note>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" variant="quiet" onClick={() => {
              const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close() }
            }}>Ouvrir en plein écran</Button>
          </div>
        </div>
      </div>

      <p className="f-help">
        La bibliothèque de modèles, le studio et le centre «&nbsp;Activité & alertes&nbsp;» (EM-01…10) sont le périmètre de Builder A (WF-11)&nbsp;;
        cette page est la surface de revue des e-mails eux-mêmes. Infrastructure d’envoi (outbox, warming, sink staging)&nbsp;: piste Technique (T3).
      </p>
    </div>
  )
}
